import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Bot, User } from "lucide-react";
import ReactMarkdown from "react-markdown";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const quickReplies = [
  "How do I become a member?",
  "What membership packages are available?",
  "How does trade facilitation work?",
  "How do I get my business verified?",
];

const botResponses: Record<string, string> = {
  "how do i become a member": "To become an AAMECC member:\n\n1. **Click Become a Member** and choose your membership type\n2. **Select a package** - Individual, Corporate, or National Chamber\n3. **Fill in your details** - name, email, phone, business info\n4. **Make payment** to activate your membership\n5. **Access your dashboard** - manage your profile and connect with the trade network\n\nOnce registered, you'll be part of the AAMECC trade community spanning 28+ countries.",
  "what membership packages are available": "AAMECC offers three membership tiers:\n\n1. **Individual** ($199) - For professionals seeking trade connections\n2. **Corporate Small Enterprise** ($2) - For small businesses joining the ecosystem\n3. **National Chamber** ($459) - For national trade chambers and large organizations\n\nAll packages include access to the member dashboard, trade directory, and networking events. Visit the **Payments** page for full details.",
  "how does trade facilitation work": "AAMECC's **Trade Facilitation** services include:\n\n- **Business matchmaking** - connecting buyers and sellers across borders\n- **Trade directory** - browse verified businesses by country and industry\n- **Government liaison** - support navigating regulations and trade policies\n- **Trade events** - summits, forums, and networking opportunities\n- **Market intelligence** - reports and insights on key markets\n\nOur platform makes it easy to discover and connect with verified trade partners.",
  "how do i get my business verified": "To get your business verified:\n\n1. **Submit business documents** - registration certificates, licenses\n2. **Provide trade references** - partners or clients who can vouch for you\n3. **Upload supporting docs** - financial records, trade history\n4. **Review period** - our team verifies within 24-48 hours\n\nVerification badges:\n- **Bronze** - email verified\n- **Silver** - documents verified\n- **Gold** - fully verified with references",
};

const defaultResponse = "Thank you for your question! I'm the AAMECC Digital assistant. I can help with:\n\n- **Membership** registration and packages\n- **Trade directory** and business listings\n- **Trade facilitation** and partnerships\n- **Events** and networking opportunities\n- **Business verification** and badges\n- **Payments** and subscription plans\n\nPlease ask me about any of these topics, or contact our support team for personalized assistance.";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Welcome to AAMECC Digital. How can I help you with trade, membership, or business connections today?" }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    const key = text.toLowerCase().trim().replace(/[?!.,]/g, "").trim();
    let response = defaultResponse;
    for (const [k, v] of Object.entries(botResponses)) {
      if (key.includes(k.replace("?", "").trim())) {
        response = v;
        break;
      }
    }

    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { role: "assistant", content: response }]);
    }, 1200 + Math.random() * 800);
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gold text-gold-foreground flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] h-[500px] bg-card border border-border shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="bg-navy p-4 flex items-center gap-3 shrink-0">
              <div className="w-8 h-8 bg-navy-light flex items-center justify-center">
                <Bot className="w-4 h-4 text-gold" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-navy-foreground">AAMECC Trade Assistant</h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-success" />
                  <span className="text-[10px] text-navy-foreground/50">Online</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-6 h-6 bg-navy flex items-center justify-center shrink-0 mt-1">
                      <Bot className="w-3 h-3 text-gold" />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-navy text-navy-foreground"
                      : "bg-secondary text-foreground"
                  }`}>
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm max-w-none [&_p]:m-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_strong]:text-foreground text-foreground">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      msg.content
                    )}
                  </div>
                  {msg.role === "user" && (
                    <div className="w-6 h-6 bg-accent flex items-center justify-center shrink-0 mt-1">
                      <User className="w-3 h-3 text-accent-foreground" />
                    </div>
                  )}
                </motion.div>
              ))}

              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-2"
                >
                  <div className="w-6 h-6 bg-navy flex items-center justify-center shrink-0">
                    <Bot className="w-3 h-3 text-gold" />
                  </div>
                  <div className="bg-secondary px-4 py-3 flex gap-1">
                    {[0, 1, 2].map(i => (
                      <motion.div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-muted-foreground"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
              <div ref={messagesEnd} />
            </div>

            {/* Quick Replies */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {quickReplies.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="text-[11px] px-3 py-1.5 border border-border text-muted-foreground hover:border-accent hover:text-accent transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-border flex gap-2 shrink-0">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
                placeholder="Ask about trade, membership..."
                className="flex-1 bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim()}
                className="w-9 h-9 bg-navy flex items-center justify-center text-navy-foreground hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
